import { HAND_BREAK_MS, TEMPO_MS } from "../batak/tempo";
import { isCard } from "../core/cards";
import { GameRuleError, } from "../core/contract";
import { at } from "../core/util";
import { botPauseFor, chooseCard } from "./bot";
import { createGame, seatScores, startNextDeck } from "./hand";
import { applyCard, getValidCards, stepOn } from "./play";
import { currentActor, PistiError } from "./state";
import { buildGameView, concealedFrom } from "./view";
/**
 * How long a capture holds the table: the two cards lie side by side, then the pile flies to
 * whoever took it, and the flight is inside this pause (as a won trick's collect is inside its
 * own). Owner, 20 Sept 2026: at 1.4 s with nothing said "who took the cards cannot be made out";
 * at 2 s with the flight after it "the cards wait too long before they go".
 */
export const SWEEP_PAUSE_MS = 1_700;
/** The first capture shows the face-down cards to the team that took them: time to read three cards. */
export const FIRST_SWEEP_PAUSE_MS = 2_800;
/** Empty hands wait a beat before the next four arrive. */
export const DEAL_PAUSE_MS = 700;
/** The deck's count is a table of its own: longer than a trick game's hand break. */
export const DECK_BREAK_MS = HAND_BREAK_MS + 3_000;
function optionsFrom(settings) {
    const options = {};
    if (settings.length !== undefined)
        options.length = settings.length;
    if (settings.goal !== undefined)
        options.goal = settings.goal;
    else if (settings.length !== undefined)
        options.goal = null;
    return options;
}
function captureEvents(state) {
    const sweep = state.sweep;
    if (sweep === null)
        return [];
    const taken = { code: "pile-taken", seat: sweep.seat, data: { cards: sweep.taken } };
    if (sweep.pisti === 0)
        return [taken];
    return [{ code: "pisti", seat: sweep.seat, data: { points: sweep.pisti } }, taken];
}
export const pistiEngine = {
    id: { family: "pisti", variant: "partnership" },
    create(seed, settings) {
        return createGame(seed, optionsFrom(settings));
    },
    actor(state) {
        return currentActor(state);
    },
    parseMove(raw) {
        if (typeof raw !== "object" || raw === null)
            return null;
        const move = raw;
        return move.kind === "play" && isCard(move.card) ? { kind: "play", card: move.card } : null;
    },
    apply(state, seat, move) {
        try {
            applyCard(state, seat, move.card);
        }
        catch (error) {
            throw new GameRuleError(error instanceof PistiError ? error.code : "move-rejected");
        }
        return captureEvents(state);
    },
    autoMove(state, seat, level) {
        return { kind: "play", card: chooseCard(state, seat, level) };
    },
    fallbackMove(state, seat) {
        const valid = getValidCards(state, seat);
        if (valid.length === 0)
            throw new GameRuleError("no-card-to-play");
        return { kind: "play", card: at(valid, 0) };
    },
    pacing(state, settings) {
        if (state.phase === "game-end")
            return { kind: "over" };
        if (state.phase === "hand-end")
            return { kind: "pause", ms: DECK_BREAK_MS, boundary: "round" };
        if (state.sweep !== null) {
            const ms = state.sweep.first ? FIRST_SWEEP_PAUSE_MS : SWEEP_PAUSE_MS;
            return { kind: "pause", ms, boundary: "step" };
        }
        const seat = currentActor(state);
        if (seat === null)
            return { kind: "pause", ms: DEAL_PAUSE_MS, boundary: "step" };
        const ms = settings.tempoMs?.playMs ?? TEMPO_MS[settings.tempo].playMs;
        return { kind: "actor", seat, ms };
    },
    botPause(state, seat) {
        return botPauseFor(state, seat);
    },
    advance(state) {
        if (state.phase === "hand-end") {
            startNextDeck(state);
            return [{ code: "hand-dealt", data: { hand: state.hand } }];
        }
        if (state.phase !== "playing")
            return [];
        const events = [];
        for (const step of stepOn(state)) {
            if (step === "dealt")
                events.push({ code: "cards-dealt", data: { round: state.dealRound } });
            const result = state.lastHandResult;
            if (step === "scored" && result !== null)
                events.push({
                    code: "hand-scored",
                    data: { t0: result.teams[0].delta, t1: result.teams[1].delta },
                });
        }
        return events;
    },
    view(state, seat) {
        return buildGameView(state, seat);
    },
    concealed(state, seat) {
        return concealedFrom(state, seat);
    },
    result(state) {
        if (state.phase !== "game-end")
            return null;
        return { scores: seatScores(state), winners: [...(state.winners ?? [])], teams: [0, 1, 0, 1] };
    },
    lastRoundRecord(state) {
        const result = state.lastHandResult;
        if (!result)
            return null;
        const cards = [0, 1, 2, 3].map((seat) => at(result.teams, seat % 2).cards);
        return { round: result.hand, payload: result, seatCounters: cards };
    },
};
//# sourceMappingURL=engine.js.map